import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as dmsService from "../../../services/dmsService";
import useFinOptions from "../../Fin/useFinOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { hint } from "../../Fin/gl/glUi";
import { DmsBadge } from "./dmsUi";
import { AppDropdown, LoadState, PageHeader, cardStyle, pageStyle, useLoad } from "../../../components/ui";

// Sales Dashboard: the pipeline, this month's orders and deliveries, stock and its age.
// A consultant sees their own figures; managers the whole dealership (or one location).
function Tile({ label, value, sub, to }) {
  const body = (
    <div style={{ ...cardStyle, padding: 16, minHeight: 96 }}>
      <div style={hint}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: "#111827" }}>{value}</div>
      {sub ? <div style={{ ...hint, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: "none" }}>
      {body}
    </Link>
  ) : (
    body
  );
}

function Bars({ rows, value, label }) {
  const max = Math.max(1, ...rows.map(value));
  return rows.map((r, i) => (
    <div key={i} className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: 13 }}>
      <div style={{ width: 160 }}>{label(r)}</div>
      <div style={{ flex: 1, background: "#F3F4F6", borderRadius: 4, height: 14 }}>
        <div style={{ width: `${(value(r) / max) * 100}%`, background: "#C9194A", height: 14, borderRadius: 4 }} />
      </div>
      <div style={{ width: 60, textAlign: "right" }}>{value(r)}</div>
    </div>
  ));
}

export default function SalesDashboard() {
  const fin = useFinOptions(["locations"]);
  const [loc, setLoc] = useState(null);
  const { data, loading, error, reload } = useLoad(() => dmsService.getDashboard({ location_id: loc || undefined }), [loc]);
  const d = data;
  return (
    <div style={pageStyle}>
      <PageHeader
        title="Sales Dashboard"
        subtitle="Pipeline, orders, deliveries and stock"
        actions={
          <div style={{ width: 220 }}>
            <AppDropdown id="dash-loc" value={loc} options={[{ label: "All locations", value: null }, ...(fin.locations || [])]} onChange={(e) => setLoc(e.value)} />
          </div>
        }
      />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {d ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
              <Tile label="Open quotations" value={d.pipeline.quotes} sub={`${fmtMoney(d.pipeline.value, 0)} in the pipeline`} to="/dms/quotes" />
              <Tile label="Orders booked this month" value={d.month.orders} sub={fmtMoney(d.month.order_value, 0)} to="/dms/orders" />
              <Tile label="Vehicles delivered this month" value={d.month.delivered} />
              <Tile label="Vehicles in stock" value={d.stock.vehicles} sub={`${fmtMoney(d.stock.cost, 0)} at cost`} to="/dms/vehicles" />
              <Tile label="Test drives today" value={d.test_drives_today} to="/dms/test-drives" />
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <div style={{ ...cardStyle, padding: 16 }}>
                  <h6>Pipeline</h6>
                  {d.pipeline.by_status.length ? (
                    d.pipeline.by_status.map((s) => (
                      <div key={s.status} className="d-flex justify-content-between mb-1" style={{ fontSize: 14 }}>
                        <DmsBadge status={s.status} />
                        <span>
                          {s.n} · {fmtMoney(s.value, 0)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={hint}>No open quotations.</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div style={{ ...cardStyle, padding: 16 }}>
                  <h6>Stock age (days in stock)</h6>
                  <Bars rows={d.aging} value={(r) => r.n} label={(r) => `${r.bucket} days`} />
                  <div style={{ ...hint, marginTop: 6 }}>Over 90 days: {fmtMoney((d.aging.find((a) => a.bucket === "90+") || {}).cost || 0, 0)} at cost</div>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div style={{ ...cardStyle, padding: 16 }}>
                  <h6>Best-selling models (90 days)</h6>
                  {d.top_models.length ? <Bars rows={d.top_models} value={(r) => r.n} label={(r) => `${r.make_name} ${r.model_name}`} /> : <div style={hint}>No orders yet.</div>}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
